import { useEffect, useId, useRef, useState } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useResubmit } from '@/hooks/useResubmit';
import { MiniFilePicker } from './MiniFilePicker';
import type { Job } from '@/types';

interface ResubmitDialogProps {
  /** The original job being revised (FR-078 parent) */
  job: Job;
  /** Controls dialog visibility */
  open: boolean;
  /** Called when dialog should close */
  onClose: () => void;
}

type FileMode = 'reuse' | 'upload';

/** FR-078: Resubmit dialog. WCAG: role=dialog, aria-modal, focus trap, ESC closes. */
export function ResubmitDialog({ job, open, onClose }: ResubmitDialogProps) {
  const titleId = useId();
  const descId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelBtnRef = useRef<HTMLButtonElement>(null);

  const [fileMode, setFileMode] = useState<FileMode>('reuse');
  const [newFile, setNewFile] = useState<File | null>(null);

  const { isSubmitting, uploadProgress, error, submit, reset } = useResubmit();

  useEffect(() => {
    if (open) { setTimeout(() => cancelBtnRef.current?.focus(), 10); }
    else { reset(); setFileMode('reuse'); setNewFile(null); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !isSubmitting) onClose(); };
    document.addEventListener('keydown', h);
    return () => { document.removeEventListener('keydown', h); };
  }, [open, isSubmitting, onClose]);

  useEffect(() => {
    if (!open) return;
    const el = dialogRef.current;
    if (!el) return;
    const getFocusable = () =>
      Array.from(
        el.querySelectorAll<HTMLElement>(
          'button:not([disabled]),[href],input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
        ),
      );
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const nodes = getFocusable();
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => { document.removeEventListener('keydown', handler); };
  }, [open]);

  if (!open) return null;

  const canSubmit = fileMode === 'reuse' || newFile !== null;
  const formattedDate = new Date(job.created_at).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;
    void submit({ jobId: job.id, file: fileMode === 'upload' ? newFile : null });
  };

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget && !isSubmitting) onClose(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative w-full max-w-md rounded-lg border border-border bg-background p-6 shadow-lg"
      >
        <h2 id={titleId} className="text-lg font-semibold text-foreground">
          Create Revised Version
        </h2>
        <p id={descId} className="mt-1 text-sm text-muted-foreground">
          Submit a corrected version linked to the original job (FR-078).
        </p>

        {/* Original job summary */}
        <div className="mt-4 rounded-md border border-border bg-muted/40 px-4 py-3 text-sm space-y-1">
          <p className="font-medium text-foreground truncate">{job.file_name}</p>
          <p className="text-muted-foreground">
            Decision: <span className="font-medium">{job.final_decision ?? 'N/A'}</span>
            {' '}&middot; Submitted: {formattedDate}
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate className="mt-5 space-y-4">
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-foreground">Document to submit</legend>

            {/* Reuse original */}
            <label className={cn('flex cursor-pointer items-start gap-2 rounded-md border px-3 py-2.5 transition-colors', fileMode === 'reuse' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30')}>
              <input type="radio" name="file-mode" value="reuse" checked={fileMode === 'reuse'}
                onChange={() => { setFileMode('reuse'); setNewFile(null); }}
                disabled={isSubmitting}
                className="mt-0.5 h-4 w-4 shrink-0 text-primary focus-visible:ring-2 focus-visible:ring-ring"
              />
              <span className="text-sm">
                <span className="font-medium text-foreground">Reuse original file</span>
                <span className="block text-xs text-muted-foreground">Re-run the pipeline on the existing document</span>
              </span>
            </label>

            {/* Upload new */}
            <label className={cn('flex cursor-pointer flex-col gap-2 rounded-md border px-3 py-2.5 transition-colors', fileMode === 'upload' ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/30')}>
              <div className="flex items-start gap-2">
                <input type="radio" name="file-mode" value="upload" checked={fileMode === 'upload'}
                  onChange={() => { setFileMode('upload'); }}
                  disabled={isSubmitting}
                  className="mt-0.5 h-4 w-4 shrink-0 text-primary focus-visible:ring-2 focus-visible:ring-ring"
                />
                <span className="text-sm">
                  <span className="font-medium text-foreground">Upload corrected file</span>
                  <span className="block text-xs text-muted-foreground">PDF, DOCX, TIFF, JPEG, or PNG — max 50 MB</span>
                </span>
              </div>
              {fileMode === 'upload' && (
                <div className="ml-6">
                  <MiniFilePicker file={newFile} onFileChange={setNewFile} disabled={isSubmitting} />
                </div>
              )}
            </label>
          </fieldset>

          {/* FR-077 human review note */}
          <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            This revision will require human review before activation (FR-077).
          </p>

          {/* Upload progress */}
          {uploadProgress !== null && uploadProgress >= 0 && (
            <div aria-live="polite" aria-atomic="true">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Uploading…</span><span>{uploadProgress}%</span>
              </div>
              <div role="progressbar" aria-valuenow={uploadProgress} aria-valuemin={0} aria-valuemax={100}
                aria-label="Upload progress" className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button ref={cancelBtnRef} type="button" variant="outline" size="sm"
              disabled={isSubmitting} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit || isSubmitting}
              aria-disabled={!canSubmit || isSubmitting}>
              {isSubmitting && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {isSubmitting ? 'Submitting…' : 'Submit Revision'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
